// src/pages/teacher/TeacherPortal.js
// Phase 4c + enrichment: two-tab portal. Take Attendance (date stepper,
// present/absent, X/Y, status badge, rescheduled-lock, unsaved guard,
// live counts) + My Classes (schedule + student history).
import React, { useState, useEffect, useMemo } from 'react';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { timeFromUTC, scheduledAtForDisplayDay, utcRangeForCalendarDay } from '../../utils/timezoneUtils';
import { Calendar, Users, Clock, LogOut, GraduationCap, ChevronRight, ChevronLeft, X, Download, FileText, FolderOpen } from 'lucide-react';

const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function to12h(hhmm) {
  if (!hhmm || !hhmm.includes(':')) return hhmm || '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function isoTime(iso, tz) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(iso));
  } catch {
    return '';
  }
}

function friendlyDateTime(iso, tz) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

// A value is "protected" (admin-owned) if it's set to something other than
// present/absent — teachers can't edit these from this screen.
const isProtected = v => !!v && v !== 'present' && v !== 'absent';

const ATT_STYLES = {
  present: { label: 'Present', dot: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50' },
  absent: { label: 'Absent', dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' },
  rescheduled: { label: 'Rescheduled', dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' },
  trial: { label: 'Trial', dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50' },
  cancelled: { label: 'Cancelled', dot: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-100' },
};

const STATUS_STYLES = {
  active: { label: 'Active', text: 'text-green-700', bg: 'bg-green-100' },
  completed: { label: 'Completed', text: 'text-blue-700', bg: 'bg-blue-100' },
  expired: { label: 'Expired', text: 'text-gray-600', bg: 'bg-gray-100' },
  pending: { label: 'Pending', text: 'text-amber-700', bg: 'bg-amber-100' },
};

function StudentHistoryModal({ childId, classId, childName, timezone, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const fns = getFunctions();
        const res = await httpsCallable(fns, 'getStudentSessionHistory')({ childId, classId });
        if (!cancelled) setInfo(res.data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load this student\'s history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [childId, classId]);

  const status = info?.status || 'unknown';
  const statusStyle = STATUS_STYLES[status] || { label: status ? cap(status) : '—', text: 'text-gray-600', bg: 'bg-gray-100' };
  const sessions = info?.sessions || [];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{childName || info?.childName || 'Student'}</h3>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
              {statusStyle.label}
            </span>
          </div>
          <button onClick={onClose} className="p-2 -m-1 text-gray-400 hover:text-gray-600 rounded-lg">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Session History</h4>
          {loading && <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>}
          {!loading && error && <p className="text-sm text-red-600 py-4">{error}</p>}
          {!loading && !error && sessions.length === 0 && (
            <p className="text-sm text-gray-400 py-6 text-center">No sessions recorded yet.</p>
          )}
          {!loading && !error && sessions.length > 0 && (
            <ul className="space-y-2">
              {sessions.map((s, i) => {
                const att = ATT_STYLES[s.attendance] || { label: s.sessionStatus === 'cancelled' ? 'Cancelled' : '—', dot: 'bg-gray-300', text: 'text-gray-500', bg: 'bg-gray-50' };
                return (
                  <li key={i} className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-gray-50">
                    <span className="text-sm text-gray-700">{friendlyDateTime(s.scheduledAt, timezone)}</span>
                    <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${att.bg} ${att.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${att.dot}`} />
                      {att.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="p-4 border-t border-gray-100">
          <button onClick={onClose} className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function MyClassesView({ classes, tz, onSelectStudent }) {
  function localSlots(slots) {
    return (slots || [])
      .map(s => {
        const { localTime, localDay } = timeFromUTC(s.time, s.dayOfWeek, tz);
        return { day: localDay, time: localTime };
      })
      .sort((a, b) => {
        const d = DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day);
        return d !== 0 ? d : a.time.localeCompare(b.time);
      });
  }

  if (classes.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
        You don't have any classes assigned yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {classes.map(cls => {
        const slots = localSlots(cls.slots);
        return (
          <div key={cls.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-800">{cls.title}</h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar size={13} />
                  {slots.length > 0 ? slots.map(s => `${cap(s.day)} ${to12h(s.time)}`).join(' · ') : 'No schedule set'}
                </span>
                {cls.duration && <span>{cls.duration} min</span>}
              </div>
            </div>
            <div className="px-5 py-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                <Users size={13} /> {cls.students.length} student{cls.students.length !== 1 ? 's' : ''}
              </div>
              {cls.students.length === 0 ? (
                <p className="text-sm text-gray-400">No students enrolled.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {cls.students.map(st => {
                    const total = st.sessionsTotal || 6;
                    const done = st.sessionsCompleted || 0;
                    const pct = Math.min(100, Math.round((done / total) * 100));
                    return (
                      <li key={st.childId}>
                        <button
                          onClick={() => onSelectStudent({ childId: st.childId, classId: cls.id, childName: st.childName })}
                          className="w-full py-2.5 flex items-center justify-between gap-4 hover:bg-gray-50 rounded-lg px-2 -mx-2 transition-colors text-left"
                        >
                          <span className="text-sm text-gray-700">{st.childName}</span>
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-purple-500" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-gray-500 tabular-nums w-12 text-right">{done}/{total}</span>
                            <ChevronRight size={16} className="text-gray-300" />
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TakeAttendanceView({ classes, tz, onSaved, onDirtyChange }) {
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [marks, setMarks] = useState({});
  const [baseline, setBaseline] = useState({});
  const [sessionMeta, setSessionMeta] = useState({});
  const [loadingDay, setLoadingDay] = useState(true);
  const [savingClass, setSavingClass] = useState(null);
  const [savedClass, setSavedClass] = useState(null);
  const [error, setError] = useState(null);

  const dayClasses = useMemo(() => {
    return (classes || [])
      .map(cls => {
        let scheduledAt = null;
        for (const s of (cls.slots || [])) {
          const iso = scheduledAtForDisplayDay(selectedDate, tz, s.dayOfWeek, s.time);
          if (iso) { scheduledAt = iso; break; }
        }
        return scheduledAt ? { ...cls, scheduledAt } : null;
      })
      .filter(Boolean);
  }, [classes, selectedDate, tz]);

  const { startISO, endISO } = useMemo(() => utcRangeForCalendarDay(selectedDate, tz), [selectedDate, tz]);
  const dayKey = dayClasses.map(c => c.id).join('|');

  useEffect(() => {
    let cancelled = false;
    async function loadDay() {
      setLoadingDay(true); setError(null);
      const nextMarks = {};
      const nextBaseline = {};
      const nextMeta = {};
      try {
        const fns = getFunctions();
        const getAtt = httpsCallable(fns, 'getSessionAttendance');
        await Promise.all(dayClasses.map(async (cls) => {
          try {
            const res = await getAtt({ classId: cls.id, dayStartISO: startISO, dayEndISO: endISO });
            const att = res.data?.attendance || {};
            nextMarks[cls.id] = { ...att };
            nextBaseline[cls.id] = { ...att };
            nextMeta[cls.id] = {
              lastMarkedByName: res.data?.lastMarkedByName || null,
              lastMarkedAt: res.data?.lastMarkedAt || null,
            };
          } catch (e) {
            nextMarks[cls.id] = {};
            nextBaseline[cls.id] = {};
            nextMeta[cls.id] = {};
          }
        }));
        if (!cancelled) { setMarks(nextMarks); setBaseline(nextBaseline); setSessionMeta(nextMeta); }
      } catch (e) {
        if (!cancelled) setError('Could not load attendance for this day.');
      } finally {
        if (!cancelled) setLoadingDay(false);
      }
    }
    loadDay();
    return () => { cancelled = true; };
    // Keyed on class IDs so the silent refresh after a save doesn't re-run this
    // and wipe unsaved marks on other classes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayKey, startISO, endISO]);

  function classHasUnsaved(classId) {
    const cur = marks[classId] || {};
    const base = baseline[classId] || {};
    const keys = new Set([...Object.keys(cur), ...Object.keys(base)]);
    for (const k of keys) {
      if ((cur[k] || null) !== (base[k] || null)) return true;
    }
    return false;
  }
  const anyUnsaved = dayClasses.some(c => classHasUnsaved(c.id));

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(anyUnsaved);
  }, [anyUnsaved, onDirtyChange]);

  function setMark(classId, childId, value) {
    setMarks(prev => ({ ...prev, [classId]: { ...(prev[classId] || {}), [childId]: value } }));
    setSavedClass(null);
  }

  function stepDay(deltaDays) {
    if (anyUnsaved && !window.confirm('You have unsaved attendance on this day. Leave without saving?')) {
      return;
    }
    setSelectedDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + deltaDays);
      return d;
    });
    setSavedClass(null);
  }

  const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());
  const selStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(selectedDate);
  const atToday = selStr >= todayStr;
  const dateLabel = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, weekday: 'long', month: 'short', day: 'numeric',
  }).format(selectedDate);

  async function saveClass(cls) {
    const classMarks = marks[cls.id] || {};
    const base = baseline[cls.id] || {};
    const records = Object.entries(classMarks)
      .filter(([childId, v]) => (v === 'present' || v === 'absent') && (base[childId] || null) !== v)
      .map(([childId, value]) => ({ childId, value }));
    if (records.length === 0) {
      setError('No new changes to save.');
      return;
    }
    setSavingClass(cls.id); setError(null); setSavedClass(null);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'submitAttendance')({
        classId: cls.id,
        scheduledAt: cls.scheduledAt,
        dayStartISO: startISO,
        dayEndISO: endISO,
        records,
      });
      setBaseline(prev => ({ ...prev, [cls.id]: { ...classMarks } }));
      setSavedClass(cls.id);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save attendance.');
    } finally {
      setSavingClass(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-2 mb-4">
        <button onClick={() => stepDay(-1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600" aria-label="Previous day">
          <ChevronLeft size={20} />
        </button>
        <div className="text-center">
          <div className="font-semibold text-gray-800">{dateLabel}</div>
          {selStr === todayStr && <div className="text-xs text-purple-600">Today</div>}
        </div>
        <button onClick={() => stepDay(1)} disabled={atToday} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Next day">
          <ChevronRight size={20} />
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm mb-4">{error}</div>}
      {loadingDay && <div className="text-center text-gray-500 py-12">Loading…</div>}
      {!loadingDay && dayClasses.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          No classes scheduled for this day.
        </div>
      )}

      {!loadingDay && dayClasses.length > 0 && (
        <div className="space-y-4">
          {dayClasses.map(cls => {
            const meta = sessionMeta[cls.id] || {};
            const saving = savingClass === cls.id;
            const justSaved = savedClass === cls.id;
            return (
              <div key={cls.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-800">{cls.title}</h2>
                  <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                    <Clock size={13} /> {isoTime(cls.scheduledAt, tz)}
                  </div>
                  {meta.lastMarkedByName && <p className="text-xs text-gray-400 mt-1">Last marked by {meta.lastMarkedByName}</p>}
                </div>
                <div className="divide-y divide-gray-100">
                  {(cls.students || []).length === 0 ? (
                    <p className="px-5 py-4 text-sm text-gray-400">No students enrolled.</p>
                  ) : (
                    cls.students.map(st => {
                      const baseVal = (baseline[cls.id] || {})[st.childId] || null;
                      const locked = isProtected(baseVal);
                      const val = (marks[cls.id] || {})[st.childId] || null;
                      const ss = STATUS_STYLES[st.status] || { label: st.status ? cap(st.status) : '—', text: 'text-gray-500', bg: 'bg-gray-100' };
                      const lockedStyle = ATT_STYLES[baseVal] || { label: cap(baseVal), dot: 'bg-gray-300', text: 'text-gray-500', bg: 'bg-gray-50' };
                      return (
                        <div key={st.childId} className="px-5 py-3 flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-700 truncate">{st.childName}</div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${ss.bg} ${ss.text}`}>{ss.label}</span>
                              <span className="text-xs text-gray-400 tabular-nums">{st.sessionsCompleted ?? 0}/{st.sessionsTotal ?? 6}</span>
                            </div>
                          </div>
                          {locked ? (
                            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${lockedStyle.bg} ${lockedStyle.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${lockedStyle.dot}`} />
                              {lockedStyle.label}
                            </span>
                          ) : (
                            <div className="flex gap-2 shrink-0">
                              <button onClick={() => setMark(cls.id, st.childId, 'present')} className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${val === 'present' ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-green-50'}`}>
                                Present
                              </button>
                              <button onClick={() => setMark(cls.id, st.childId, 'absent')} className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${val === 'absent' ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-red-50'}`}>
                                Absent
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="px-5 py-3 bg-gray-50 flex items-center justify-between gap-3">
                  {justSaved ? <span className="text-sm text-green-600 font-medium">Saved ✓</span> : <span className="text-xs text-gray-400">Tap Present or Absent, then Save</span>}
                  <button onClick={() => saveClass(cls)} disabled={saving} className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save Attendance'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TeacherResourcesView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const fns = getFunctions();
        const res = await httpsCallable(fns, 'getTeacherResources')();
        if (!cancelled) setResources(res.data?.resources || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load resources.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const groups = {};
    resources.forEach(r => {
      const cats = (r.categories && r.categories.length) ? r.categories : ['Other'];
      cats.forEach(c => {
        if (!groups[c]) groups[c] = [];
        groups[c].push(r);
      });
    });
    return groups;
  }, [resources]);

  const categoryNames = Object.keys(grouped).sort();

  function fmtSize(bytes) {
    if (!bytes) return '';
    const mb = bytes / 1024 / 1024;
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  }

  return (
    <div>
      {loading && <div className="text-center text-gray-500 py-12">Loading…</div>}
      {!loading && error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}
      {!loading && !error && resources.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          No resources available yet.
        </div>
      )}
      {!loading && !error && categoryNames.length > 0 && (
        <div className="space-y-6">
          {categoryNames.map(cat => (
            <div key={cat}>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                <FolderOpen size={13} /> {cat}
              </div>
              <div className="space-y-2">
                {grouped[cat].map(r => (
                  <div key={r.id} className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <FileText size={18} className="text-purple-600 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{r.title || r.fileName}</div>
                        {r.description && <div className="text-xs text-gray-500 mt-0.5">{r.description}</div>}
                        {r.fileSize > 0 && <div className="text-[11px] text-gray-400 mt-0.5">{fmtSize(r.fileSize)}</div>}
                      </div>
                    </div>
                    {r.fileUrl ? (
                      <a href={r.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 shrink-0">
                        <Download size={15} /> Download
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 shrink-0">Unavailable</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TeacherPortal() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('attendance');
  const [attendanceDirty, setAttendanceDirty] = useState(false);

  useEffect(() => {
    const KEY = 'teacherLoginRecorded';
    if (!sessionStorage.getItem(KEY)) {
      const fns = getFunctions();
      httpsCallable(fns, 'recordTeacherLogin')()
        .then(() => sessionStorage.setItem(KEY, '1'))
        .catch(err => console.error('recordTeacherLogin failed:', err));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const fns = getFunctions();
        const res = await httpsCallable(fns, 'getTeacherSchedule')();
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load your schedule.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const tz = data?.teacher?.timezone || 'UTC';

  async function refreshSchedule() {
    try {
      const fns = getFunctions();
      const res = await httpsCallable(fns, 'getTeacherSchedule')();
      setData(res.data);
    } catch (err) {
      console.error('Schedule refresh failed:', err);
    }
  }

  function switchTab(next) {
    if (tab === 'attendance' && next !== 'attendance' && attendanceDirty) {
      if (!window.confirm('You have unsaved attendance. Leave without saving?')) return;
    }
    setTab(next);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="text-purple-600" size={22} />
            <div>
              <h1 className="text-lg font-bold text-gray-800">Teacher Portal</h1>
              {data?.teacher?.name && <p className="text-xs text-gray-500">{data.teacher.name}</p>}
            </div>
          </div>
          <button onClick={() => signOut(auth)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <LogOut size={15} /> Sign out
          </button>
        </div>
        <div className="max-w-3xl mx-auto px-4 flex gap-1">
          <button onClick={() => switchTab('attendance')} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'attendance' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Take Attendance
          </button>
          <button onClick={() => switchTab('classes')} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'classes' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            My Classes
          </button>
          <button onClick={() => switchTab('resources')} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'resources' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Resources
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {loading && <div className="text-center text-gray-500 py-16">Loading…</div>}
        {!loading && error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}
        {!loading && !error && data && (
          <>
            {tab !== 'resources' && (
              <p className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
                <Clock size={13} /> Times shown in your timezone ({tz})
              </p>
            )}
            {tab === 'attendance' && <TakeAttendanceView classes={data.classes} tz={tz} onSaved={refreshSchedule} onDirtyChange={setAttendanceDirty} />}
            {tab === 'classes' && <MyClassesView classes={data.classes} tz={tz} onSelectStudent={setSelected} />}
            {tab === 'resources' && <TeacherResourcesView />}
          </>
        )}
      </div>

      {selected && (
        <StudentHistoryModal
          childId={selected.childId}
          classId={selected.classId}
          childName={selected.childName}
          timezone={tz}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

export default TeacherPortal;
